import React, { useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { API_URL } from '../../utils/api';
import { buildHandoffPayload, buildHandoffUrl, isPhoneScreen, prepareHandoffLoops } from '../../utils/boothHandoff';
import { bpmFromStyle } from '../../utils/reflectFlow';

const NOTES = {
  phone: 'Beat Arranger runs on a laptop or tablet. Open The Booth there to send this song.',
  tooLong: "This song is too long to send. Copy everything and paste it in Beat Arranger's Lyrics panel.",
  blocked: 'Your browser blocked the new tab. Allow pop-ups for this site and try again.',
};

// Fresh preview URLs for the chosen loops: signed links expire, so never reuse saved ones.
const chosenLoops = async (beat) => {
  if (!beat?.pack_id) return [];
  const response = await fetch(`${API_URL}/api/sound-packs/${beat.pack_id}/loops`);
  if (!response.ok) throw new Error('Selected beat could not load');
  const groups = await response.json();
  const all = Object.values(groups || {}).flat();
  return [beat.drums_id, beat.melody_id].filter(Boolean).map(id => {
    const loop = all.find(item => item.loop_id === id);
    if (!loop) throw new Error('Selected loop is unavailable');
    return loop;
  });
};

const SendToArranger = ({ song, className = '' }) => {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const send = async () => {
    setNote('');
    if (isPhoneScreen()) {
      setNote(NOTES.phone);
      return;
    }
    setBusy(true);
    let loops;
    try {
      loops = await prepareHandoffLoops(await chosenLoops(song.beat));
    } catch (_error) {
      setBusy(false);
      setNote('The selected audio could not be loaded or its musical length does not match its tempo. Check the beat and try again.');
      return;
    }
    setBusy(false);
    const result = buildHandoffUrl(buildHandoffPayload(song, bpmFromStyle(song.style) || 90, loops));
    if (result.tooLong) {
      setNote(NOTES.tooLong);
      return;
    }
    const opened = window.open(result.url, 'ltbb_beat_arranger');
    if (opened) opened.opener = null;
    else setNote(NOTES.blocked);
  };

  return (
    <div className={className}>
      <button
        type="button"
        onClick={send}
        disabled={busy}
        className="flex min-h-12 items-center gap-2 rounded-full border border-[color:var(--glow)]
          px-5 text-base font-black hover:bg-white/5 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ExternalLink className="h-5 w-5" />}
        Take it to Beat Arranger
      </button>
      {note && <p role="status" className="mt-2 max-w-sm text-sm font-bold text-white/65">{note}</p>}
    </div>
  );
};

export default SendToArranger;
